// File: src/pages/ConferenceProgramPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Card,
  Button,
  Row,
  Col,
  Tag,
  Space,
  Input,
  message,
  Empty,
  Grid,
} from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  LinkOutlined,
  FilePdfOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import IcastHeader from "../Layouts/IcastHeader";
import Footer from "../Layouts/IcastFooter";

const { Title, Paragraph } = Typography;
const { useBreakpoint } = Grid;

/** 🔧 Add or edit resources here */
const resourcesSeed = [
  {
    id: "overview-2025-08-11",
    title: "Conference Overview",
    description: "High-level schedule and venue snapshot for ICAST 2025",
    url: "https://icast.pk/PDF/ICAST 2025 - Conference Program.pdf",
    type: "PDF",
    size: "1–2 MB (approx.)",
    updated: "2025-11-08",
    featured: true,
  },
  // {
  //   id: "overview-2025--11",
  //   title: "Conference Book",
  //   description: "High-level schedule and venue snapshot for ICAST 2025",
  //   url: "https://icast.pk/PDF/ICAST Conference Book (Website) (1).pdf",
  //   type: "PDF",
  //   size: "1–2 MB (approx.)",
  //   updated: "2025-11-08",
  //   featured: true,
  // },
];

const ConferenceProgramPage = () => {
  const screens = useBreakpoint();

  const titleFontSize = screens.xl
    ? "48px"
    : screens.lg
    ? "48px"
    : screens.md
    ? "46px"
    : "36px";

  const [query, setQuery] = useState("");
  const [resources] = useState(resourcesSeed);

  useEffect(() => {
    document.title = "Conference Program | ICAST 2025";
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return resources.filter((r) =>
      q
        ? [r.title, r.description, r.type].some((v) =>
            String(v || "")
              .toLowerCase()
              .includes(q)
          )
        : true
    );
  }, [query, resources]);

  const handleView = (url) => {
    window.open(encodeURI(url), "_blank", "noopener,noreferrer");
  };

  const handleDownload = (url) => {
    window.open(encodeURI(url), "_blank", "noopener,noreferrer");
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(encodeURI(url));
      message.success("Link copied to clipboard");
    } catch {
      message.error("Could not copy link");
    }
  };

  return (
    <>
      <IcastHeader />
      <Title
        style={{
          textAlign: "justify",
          padding: "20px 40px",
          fontFamily: "Inter, sans-serif",
          fontWeight: "700",
          fontSize: titleFontSize,
          backgroundColor: "#EEF0F2",
        }}
      >
        Conference Program
      </Title>

      {/* Hero Section */}
      <div className="cpp-hero">
        <div className="cpp-hero-inner">
          <Paragraph className="cpp-hero-sub">
            Browse official documents for ICAST 2025 — program overview, agenda,
            and resources. More downloads will appear here as they’re published.
          </Paragraph>
        </div>
      </div>

      {/* Content Section */}
      <div className="cpp-wrap">
        {filtered.length === 0 ? (
          <Card
            className="cpp-card"
            style={{ maxWidth: 980, margin: "0 auto" }}
          >
            <Empty
              description={
                <span>
                  No results. Try a different search term or clear it.
                </span>
              }
            />
          </Card>
        ) : (
          <Row gutter={[20, 20]}>
            {filtered.map((item) => (
              <Col key={item.id} xs={24} sm={12} lg={8}>
                <Card
                  className="cpp-card"
                  hoverable
                  title={
                    <Space align="center">
                      <FilePdfOutlined className="cpp-icon" />
                      <span className="cpp-card-title">{item.title}</span>
                      {item.featured && <Tag color="green">Featured</Tag>}
                    </Space>
                  }
                >
                  <Paragraph className="cpp-card-desc">
                    {item.description}
                  </Paragraph>

                  <Space size={[8, 8]} wrap>
                    <Tag>{item.type}</Tag>
                    {item.size && <Tag>{item.size}</Tag>}
                    {item.updated && (
                      <Tag color="blue">Updated: {item.updated}</Tag>
                    )}
                  </Space>

                  <div className="cpp-buttons">
                    <Space wrap>
                      <Button
                        icon={<EyeOutlined />}
                        onClick={() => handleView(item.url)}
                      >
                        View
                      </Button>
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(item.url)}
                        style={{ backgroundColor: "#006814", border: "none" }}
                      >
                        Download
                      </Button>
                      <Button
                        icon={<LinkOutlined />}
                        onClick={() => handleCopy(item.url)}
                      >
                        Copy link
                      </Button>
                    </Space>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>

      <Footer />
      

      {/* Inline CSS */}
      <style>{`
        .cpp-hero {
          background:
            radial-gradient(1200px 400px at 50% -200px, #dfe7ff 0%, transparent 70%),
            #EFF0F2;
          padding: 56px 16px 24px;
        }
        .cpp-hero-inner {
          max-width: 1100px;
          margin: 0 auto;
          text-align: center;
        }
        .cpp-hero-sub {
          color: #000000ff;
          margin: 0 auto 24px;
          max-width: 800px;
          font-size: 24px;
        }
        .cpp-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .cpp-search {
          max-width: 520px;
          width: 100%;
          background: #fff;
        }

        .cpp-wrap {
          max-width: 1100px;
          margin: 0 auto;
          padding: 24px 16px 56px;
        }

        .cpp-card {
          border-radius: 16px !important;
          box-shadow: 0 16px 48px rgba(0,0,0,0.10) !important;
        }
        .cpp-card-title {
          font-weight: 600;
          font-size: 16px;
        }
        .cpp-card-desc {
          color: #4b5563;
          min-height: 44px;
        }
        .cpp-buttons {
          margin-top: 12px;
        }
        .cpp-icon {
          color: #e73d2d;
          font-size: 18px;
        }

        @media (max-width: 768px) {
          .cpp-hero-sub { font-size: 16px !important; }
          .cpp-wrap { padding: 16px 12px 40px; }
        }
      `}</style>
    </>
  );
};

export default ConferenceProgramPage;
